/**
 * Ce que l'écran « Fournisseurs » dit d'une clé et d'un test : logique pure.
 * La passerelle ne rend jamais une clé, seulement ses 4 derniers caractères,
 * sa date et son auteur.
 */
import type { KeyTestResult, ProviderKeyState } from "../../models/domain";

const dateFormat = new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" });

/** « •••• 9fQa · posée le 14/10/2026 10:02 par Karim » ; null = aucune clé. */
export const keyLabel = (key: ProviderKeyState | undefined): string | null => {
  if (!key?.key_set) return null;
  const parts = [`•••• ${key.last4 ?? ""}`.trim()];
  if (key.set_at) {
    const by = key.set_by_name || (key.set_by ? `compte n° ${key.set_by}` : "");
    parts.push(`posée le ${dateFormat.format(new Date(key.set_at))}${by ? ` par ${by}` : ""}`);
  }
  return parts.join(" · ");
};

/** Le résultat d'un test de clé, en clair, et s'il est bon (clé reconnue par le fournisseur). */
export const testVerdict = (t: KeyTestResult): { ok: boolean; text: string } => {
  const latency = `${t.latency_ms.toLocaleString("fr-FR")} ms`;
  switch (t.class) {
    case "ok":
      return { ok: true, text: `Clé acceptée (${latency}).` };
    case "quota":
      return { ok: true, text: `Clé reconnue, mais le crédit du compte est épuisé (${latency}).` };
    case "rate_limit":
      return { ok: true, text: `Clé reconnue, débit limité pour l'instant (${latency}).` };
    case "auth":
      return { ok: false, text: `Clé refusée par le fournisseur${t.http_status ? ` (HTTP ${t.http_status})` : ""}.` };
    case "network":
      return { ok: false, text: "Fournisseur injoignable depuis la passerelle (réseau)." };
    default: {
      const details = [t.http_status ? `HTTP ${t.http_status}` : "", t.error_code ?? ""].filter(Boolean).join(", ");
      return { ok: false, text: `Réponse inattendue du fournisseur${details ? ` (${details})` : ""}.` };
    }
  }
};
